import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Phone } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import FleetStrip from "../components/FleetStrip";
import { Section, SectionHeading } from "../components/Section";
import Reveal from "../components/Reveal";
import { todayISO } from "../components/BookingWidget";
import { FLEET_TYPES, LOCATIONS, REQUIREMENTS } from "../lib/content";
import { useHours } from "../hooks/useHours";

const WB = LOCATIONS["west-branch"];
const LM = LOCATIONS["lake-milton"];

export default function RentalsHub() {
  const [date] = useState(todayISO());
  const { hoursFor, loading } = useHours();

  return (
    <>
      <Seo
        title="Boat Rentals | West Branch & Lake Milton State Parks, Ohio"
        description="Compare boat rentals at West Branch State Park, where online booking is live, and Lake Milton State Park, where reservations are taken by phone. Pontoons, tritoons, jet skis, kayaks and fishing boats."
        path="/boat-rentals"
        image="/images/tritoon_high_hp.jpeg"
      />

      <PageHero
        image="/images/tritoon_high_hp.jpeg"
        alt="High-horsepower tritoon running across the lake"
        eyebrow="Boat rentals"
        title="Two lakes, one fleet standard"
        subtitle="West Branch State Park and Lake Milton State Park run the same way — the same boats, the same rules, the same crew standards."
      />

      <Section tone="white">
        <SectionHeading eyebrow="Choose your marina" title="Where would you like to launch?" />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {[WB, LM].map((loc, i) => {
            const h = hoursFor(loc.slug);
            return (
              <Reveal
                key={loc.slug}
                delay={i * 90}
                data-testid={`hub-location-card-${loc.slug}`}
                className="flex h-full flex-col overflow-hidden rounded-3xl border border-sand-border bg-sand"
              >
                <img
                  src={loc.slug === "west-branch" ? "/images/family_pontoon_deck.jpeg" : "/images/marina_docks.jpeg"}
                  alt={
                    loc.slug === "west-branch"
                      ? "Family on a pontoon deck at West Branch"
                      : "Boats moored along the docks at Lake Milton"
                  }
                  loading="lazy"
                  width={1264}
                  height={848}
                  className="w-full object-cover"
                  style={{ aspectRatio: "16 / 9" }}
                />
                <div className="flex flex-1 flex-col p-7">
                  <p className="eyebrow text-lake">{loc.park}</p>
                  <h3 className="mt-3 font-display text-2xl font-bold text-navy">{loc.name}</h3>
                  <p className="mt-3 text-sm text-slate-600" data-testid={`hub-hours-${loc.slug}`}>
                    {h ? `Daily ${h.open} – ${h.close}` : loading ? "Hours loading…" : "Call for hours"}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-slate-700">
                    {loc.onlineBooking
                      ? "Live availability and online booking. Pick a date and see every boat that can go out."
                      : "Reservations for this location are taken over the phone. Online booking is on the way."}
                  </p>
                  <div className="mt-auto flex flex-wrap gap-3 pt-7">
                    <Link
                      to={`/boat-rentals/${loc.slug}`}
                      data-testid={`hub-link-${loc.slug}`}
                      className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-navy-surface"
                    >
                      {loc.onlineBooking ? "See available boats" : "Location details"}
                    </Link>
                    <a
                      href={loc.phoneHref}
                      data-testid={`hub-call-${loc.slug}`}
                      className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-sand-card"
                    >
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      {loc.phone}
                    </a>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <FleetStrip
        locationId={WB.locationId}
        slug={WB.slug}
        date={date}
        heading="Available today at West Branch"
        intro="Pulled live from our booking system. Pick a different date on the West Branch page to see that day's boats."
      />

      <Section tone="white">
        <SectionHeading
          eyebrow="The fleet"
          title="What we rent"
          intro="Boat types available across our two marinas. Exact boats on any given day come from live availability."
        />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FLEET_TYPES.map((t, i) => (
            <Reveal
              key={t.name}
              delay={i * 60}
              data-testid={`fleet-type-${i}`}
              className="overflow-hidden rounded-2xl border border-sand-border bg-sand"
            >
              <img
                src={t.image}
                alt={t.alt}
                loading="lazy"
                width={1264}
                height={848}
                className="w-full object-cover"
                style={{ aspectRatio: "16 / 9" }}
              />
              <div className="p-6">
                <h3 className="font-display text-lg font-semibold text-navy">{t.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Before you book" title="The rules in brief" />
        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
          {REQUIREMENTS.slice(0, 4).map((r, i) => (
            <Reveal
              key={r.id}
              delay={i * 60}
              as="li"
              className="flex gap-3 rounded-2xl border border-sand-border bg-white p-5"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-lake" aria-hidden="true" />
              <div>
                <h3 className="font-display font-semibold text-navy">{r.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{r.body}</p>
              </div>
            </Reveal>
          ))}
        </ul>
        <Link
          to="/rental-requirements"
          data-testid="hub-all-requirements-link"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-ember-hover"
        >
          Read all rental requirements
        </Link>
      </Section>
    </>
  );
}
